import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { linkService } from '../../services/linkService';
import { analyticsService } from '../../services/analyticsService';

const RedirectPage = () => {
  const { shortCode } = useParams();
  const [link, setLink] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    handleRedirect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortCode]);

  const handleRedirect = async () => {
    try {
      // Find the link by shortCode
      const foundLink = linkService.getByShortCode(shortCode);
      
      if (!foundLink) {
        setError('Link not found');
        return;
      }

      setLink(foundLink);

      // Track the click
      await trackClick(foundLink);

      // Increment click count
      linkService.incrementClicks(foundLink.id);

      // Delay redirect slightly to ensure analytics are captured
      setTimeout(() => {
        window.location.href = foundLink.originalUrl;
      }, 500);

    } catch (error) {
      console.error('Redirect error:', error);
      setError('An error occurred while redirecting');
    }
  };

  const trackClick = async (link) => {
    try {
      // Get user's info for analytics
      const userAgent = navigator.userAgent;
      const referrer = document.referrer;
      const timestamp = new Date().toISOString();
      
      // Determine device type
      const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
      const isTablet = /iPad/i.test(userAgent);
      const device = isMobile ? (isTablet ? 'tablet' : 'mobile') : 'desktop';
      
      // Get browser info
      const browser = getBrowser(userAgent);
      
      // Create click event
      const clickEvent = {
        id: Date.now().toString(),
        linkId: link.id,
        timestamp,
        device,
        browser,
        referrer: referrer || 'direct',
        userAgent,
        // Hash IP for privacy (in real app, would be done server-side)
        ipHash: await hashIP()
      };

      // Record the click
      await analyticsService.recordClick(clickEvent);

    } catch (error) {
      console.error('Analytics tracking error:', error);
      // Don't block redirect on analytics failure
    }
  };

  const getBrowser = (userAgent) => {
    if (userAgent.includes('Firefox')) return 'Firefox';
    if (userAgent.includes('Chrome')) return 'Chrome';
    if (userAgent.includes('Safari')) return 'Safari';
    if (userAgent.includes('Edge')) return 'Edge';
    return 'Other';
  };

  const hashIP = async () => {
    try {
      // Get user's IP from a service
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      
      // Simple hash for demo (in production, use proper server-side hashing)
      const hash = btoa(data.ip + 'salt').substring(0, 10);
      return hash;
    } catch (error) {
      return 'unknown';
    }
  };

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>🔗</div>
          <h1 style={styles.title}>Link Not Found</h1>
          <p style={styles.message}>
            The link you're looking for doesn't exist or has been removed.
          </p>
          <button 
            style={styles.button}
            onClick={() => window.location.href = '/'}
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.loadingIcon}>
          <div style={styles.spinner}></div>
        </div>
        
        <h1 style={styles.title}>Redirecting...</h1>
        
        {link && (
          <div style={styles.linkInfo}>
            <p style={styles.message}>
              Taking you to: <br />
              <strong>{link.title || link.originalUrl}</strong>
            </p>
            
            <div style={styles.urlPreview}>
              {link.originalUrl}
            </div>
            
            <p style={styles.fallback}>
              If you're not redirected automatically, 
              <a 
                href={link.originalUrl}
                style={styles.link}
                rel="noopener noreferrer"
              >
                click here
              </a>
            </p>
          </div>
        )}
        
        <div style={styles.branding}>
          <span style={styles.brandText}>Powered by ShortLink</span>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .spinner {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '40px',
    maxWidth: '500px',
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    border: '1px solid #e2e8f0'
  },
  loadingIcon: {
    marginBottom: '24px'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    margin: '0 auto'
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '16px',
    margin: 0
  },
  message: {
    fontSize: '16px',
    color: '#6b7280',
    marginBottom: '24px',
    lineHeight: '1.6'
  },
  linkInfo: {
    marginBottom: '32px'
  },
  urlPreview: {
    backgroundColor: '#f1f5f9',
    padding: '12px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#475569',
    fontFamily: 'monospace',
    margin: '16px 0',
    wordBreak: 'break-all'
  },
  fallback: {
    fontSize: '14px',
    color: '#9ca3af',
    marginTop: '16px'
  },
  link: {
    color: '#3b82f6',
    textDecoration: 'underline',
    marginLeft: '4px'
  },
  button: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 24px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  branding: {
    marginTop: '32px',
    paddingTop: '16px',
    borderTop: '1px solid #e5e7eb'
  },
  brandText: {
    fontSize: '12px',
    color: '#9ca3af',
    textDecoration: 'none'
  }
};

export default RedirectPage;